import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Caption1, MessageBar, MessageBarBody, Spinner, Text, makeStyles } from '@fluentui/react-components';
import {
  findStepType, portalFormGroupHeading, portalFormGroups,
  type PersonStepSummary, type PortalFormGroupId, type StepState,
} from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { fetchPersonSteps } from '@/lib/api.js';
import { StepAnswersPanel } from './StepAnswersPanel.js';

/**
 * The forms this person was sent, and HR opening any one of them (ADR-013 §4, #211).
 *
 * A row that is here is a row this caller may read: the API OMITS a segregated form from callers
 * without `answers.restricted.read` rather than returning a locked placeholder, because a
 * placeholder still says one exists. So there is deliberately no "restricted" empty row to render,
 * and no permission branching in this component at all — it renders what it was given.
 *
 * GROUPED AND NUMBERED as frame `323:1347` draws it (#372 part 3): the forms under their 3.1
 * headings, each with the number the candidate sees on it.
 *
 * THE NUMBERS ARE THE SERVER'S, not this component's. It numbers the list in the order it was
 * given, and the API sorts with `orderPortalForms` before sending — so HR's "form 4" and the
 * candidate's "Form 4 of 22" are the same form. Sorting again here would be a second opinion, and
 * the first time the two disagreed nobody would know which screen was wrong.
 *
 * The total is what this person actually has, which is Paul's decision of 2026-09-30: his frames
 * number against a finished portal of 22 forms, and far fewer exist today.
 *
 * VISUAL PASS STILL OWED. Figma 06b forms 3.2–3.19 draw the panel that opens from a row. The list
 * now follows the frame's structure; the row and panel styling is a separate pass, and saying so
 * here beats implying the design has been met.
 */

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    paddingBlock: tokens.spacingVerticalXS,
  },
  grow: { flex: '1 1 12rem', minWidth: 0, display: 'flex', flexDirection: 'column' },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  groupHead: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginBlockStart: tokens.spacingVerticalS },
  /** Fixed width so the names line up down the list rather than stepping in at 10. */
  number: { flex: '0 0 1.5rem', textAlign: 'right' },
  open: { flex: '0 0 auto' },
  answers: {
    // Longhands, not `borderInlineStart`: Griffel rejects the shorthand (it cannot reason about
    // which longhand a later rule overrides), and the type error says so only in the web build.
    borderInlineStartWidth: tokens.strokeWidthThick,
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: tokens.colorNeutralStroke2,
    paddingInlineStart: tokens.spacingHorizontalM,
    marginBlockStart: tokens.spacingVerticalS,
  },
});

/**
 * Words for a stored state, HR's vocabulary rather than the enum's.
 *
 * `cancelled` is included because a retained instance can carry it and a row with no words at all
 * reads as a bug. Exhaustive by type: adding a state to the kernel stops this compiling.
 */
const STATE_WORDS: Record<StepState, string> = {
  not_started: 'Not started',
  sent: 'Sent, not opened',
  in_progress: 'In progress',
  submitted: 'Awaiting review',
  approved: 'Reviewed',
  rejected: 'Returned to the candidate',
  cancelled: 'Cancelled',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The most recent thing that happened, so a row says where it stands without a second line each. */
function whenLine(step: PersonStepSummary): string | null {
  if (step.reviewedAt !== null) return `Reviewed ${formatDate(step.reviewedAt)}`;
  if (step.submittedAt !== null) return `Sent ${formatDate(step.submittedAt)}`;
  if (step.startedAt !== null) return `Started ${formatDate(step.startedAt)}`;
  return null;
}

export interface PersonFormsPanelProps {
  readonly personId: string;
  /** The banner inside the panel names the candidate (ADR-013 §4). */
  readonly candidateFirstName: string;
}

export function PersonFormsPanel({ personId, candidateFirstName }: PersonFormsPanelProps) {
  const styles = useStyles();
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useQuery({ queryKey: ['person-steps', personId], queryFn: () => fetchPersonSteps(personId), retry: false });

  if (query.isPending) return <Spinner size="tiny" label="Loading forms" />;
  if (query.isError) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>This person’s forms could not be loaded.</MessageBarBody>
      </MessageBar>
    );
  }

  const steps = query.data;
  if (steps.length === 0) {
    return <Caption1>No forms have been sent to this person yet.</Caption1>;
  }

  /*
    Numbered over the WHOLE list first, so a form's number is its number wherever it is shown — then
    split into groups for display. Numbering within each group instead would restart at 1 under
    every heading and stop matching the candidate's form header, which is the one thing these two
    screens must agree on.
  */
  const numbered = steps.map((step, at) => ({ step, number: at + 1 }));
  const grouped = portalFormGroups
    .map((group) => ({ group, rows: numbered.filter((row) => row.step.portalGroup === group.id) }))
    .filter((entry) => entry.rows.length > 0);
  /* A form whose group is unknown is listed, under no heading, rather than silently dropped. */
  const ungrouped = numbered.filter((row) => !portalFormGroups.some((group) => group.id === row.step.portalGroup));

  return (
    <div className={styles.panel}>
      <Text weight="semibold">{`Onboarding forms in the portal (${steps.length})`}</Text>
      {/*
        The frame's sentence under the heading, minus the half this screen cannot say. Paul's reads
        "Worked out from Marcus's role (Home Health Aide) and tag (drives on Agency business)" — the
        packet IS derived that way, but this component is given a person id and a first name, not a
        role, so naming the role here would mean passing it in to restate something the record
        already shows above. The part that is this list's own job is kept.
      */}
      <Caption1>
        Web versions of the paper forms the Policies &amp; Procedures require: fill in, read and
        sign, review and sign, and upload.
      </Caption1>
      {grouped.map(({ group, rows }) => (
        <div key={group.id} className={styles.group}>
          <div className={styles.groupHead}>
            <Text weight="semibold">{portalFormGroupHeading(group.id as PortalFormGroupId, rows.map((row) => row.number))}</Text>
            <Badge appearance="tint" aria-label={`${rows.length} in this group`}>{rows.length}</Badge>
          </div>
          {rows.map(({ step, number }) => renderRow(step, number))}
        </div>
      ))}
      {ungrouped.map(({ step, number }) => renderRow(step, number))}
    </div>
  );

  function renderRow(step: PersonStepSummary, number: number) {
    const kernel = findStepType(step.type);
    const open = openId === step.id;
    const when = whenLine(step);
    return (
      <div key={step.id}>
        <div className={styles.row}>
          {/*
            The number the candidate sees on this form. Shown here so HR reading "form 12" in a
            message and HR reading this list are looking at the same row.
          */}
          <Caption1 className={styles.number}>{number}</Caption1>
          <div className={styles.grow}>
            {/*
              The form's own name where the definition gives one — "Your driver's license" rather
              than "Upload", which is what the candidate is looking at. A retired definition still
              has a row: the step type, then its id, is the only honest label left.
            */}
            <Text weight="semibold">{step.heading ?? kernel?.label ?? step.type}</Text>
            <Caption1>
              {/*
                The policy this form comes from, first (#357, frame `323:1347`). It is the
                requirement's own citation, not Paul's hand-written sentence — he writes "Front and
                back" and "Because Marcus drives on Agency business", and that copy exists nowhere
                in the catalog. The source is what the handoff's target-list column is called, and
                it is the part HR can look up.
              */}
              {step.source === null ? '' : `${step.source} · `}
              {STATE_WORDS[step.state]}
              {when === null ? '' : ` · ${when}`}
              {step.attempt > 0 ? ` · attempt ${step.attempt + 1}` : ''}
            </Caption1>
          </div>
          {/*
            Badged only where it is true. Every row here is readable by this caller, so the badge
            says "handle this carefully", never "you may not see this".
          */}
          {step.segregated ? <Badge appearance="outline" color="danger">Restricted</Badge> : null}
          <Button
            className={styles.open}
            appearance="secondary"
            size="small"
            aria-expanded={open}
            onClick={() => setOpenId(open ? null : step.id)}
          >
            {open ? 'Close' : 'Open'}
          </Button>
        </div>
        {open ? (
          <div className={styles.answers}>
            <StepAnswersPanel
              personId={personId}
              instanceId={step.id}
              type={step.type}
              config={step.config}
              candidateFirstName={candidateFirstName}
            />
          </div>
        ) : null}
      </div>
    );
  }
}
